import React, { useState, useEffect } from 'react';
import { inject, observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import * as routes from 'routes';
import { API_ACCOUNT_COMMISSIONS } from 'api/endpoints';

import Button from '@material-ui/core/Button';
import EditIcon from '@material-ui/icons/EditOutlined';
import ExposureIcon from '@material-ui/icons/Exposure';
import { MTableToolbar } from '@material-table/core';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import RefreshIcon from '@material-ui/icons/Refresh';
import useStyles, { centerCellStyles, rightCellStyles } from './styles/Accounts';
import DetailIcon from '@material-ui/icons/DescriptionOutlined';
import Typography from '@material-ui/core/Typography';
import { useTheme } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import GetAppIcon from '@material-ui/icons/GetApp';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { Theme } from '@material-ui/core/styles/createTheme';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';
import ErrorIcon from '@material-ui/icons/Error';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';

import AccessNone from 'components/Icons/AccessNoneIcon';
import AccessFull from 'components/Icons/AccessFullIcon';
import FilterIcon from 'components/Icons/FilterIcon';
import NoteIcon from 'components/Icons/Note';

import {
  compareCzechDates,
  formatAmountValue,
  isDraftOrMock as isDraftOrMockCheck,
  searchInStrings,
  compareAmountValues,
} from 'helpers/helpers';
import MaterialTable from 'components/Table/Table';
import Tooltip from 'components/Tooltip/Tooltip';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import LoadingIndicator from 'components/Loading/LoadingIndicator';

import { Permission } from 'helpers/permissions';
import { AccountPageTypes, AccountsStore, DraftItem, TwoFactorStatus } from 'stores/accountsStore';
import { RootStore } from 'stores/rootStore';
import GenerationDialog from 'components/GenerationDialog/GenerationDialog';
import { Widths } from './Accounts';
import Commissions, { CommissionData } from './Commissions/Commissions';
import { EdwardColors, ClientView, AccountStatus, Order } from 'enums';

function getStatusTooltipKey(status: string, isProtected: boolean, realm: string): string {
  const prefix = 'accounts_tooltip_';

  if (isProtected) {
    return `${prefix}${realm === 'bt' ? 'PROTECTED_BT' : 'PROTECTED'}`;
  }

  return `${prefix}${status}`;
}

interface Props {
  newAccountsStore?: AccountsStore;
  rootStore?: RootStore;
}

const Drafts: React.FC<Props> = ({ newAccountsStore, rootStore }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const {
    drafts,
    cancelAccount,
    copyAccount,
    downloadAccounts,
    pageSize,
    onChangeRowsPerPage,
    accountsState,
    onTableSearchChange,
    onTableOrderChange,
    sortColumns,
  } = newAccountsStore!;
  const theme = useTheme();
  const [filtersVisible, setFilterVisible] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState<DraftItem | null>(null);
  const [commissionsOpen, setCommissionsOpen] = useState(false);
  const [commission, setCommission] = useState<CommissionData | null>(null);
  const [defaultView, setDefaultView] = useState(!newAccountsStore?.accountsState[AccountPageTypes.DRAFTS].cols);

  const { palette } = theme;
  const state = accountsState[AccountPageTypes.DRAFTS];

  useEffect(() => {
    rootStore!.stores.uiStore.setAccountsListData({ newAccountsStore, tab: AccountPageTypes.DRAFTS });
    return () => {
      rootStore!.stores.uiStore.unsetAccountsListData();
    };
  });

  const userModified = newAccountsStore?.accountsState[AccountPageTypes.DRAFTS].userModified;

  useEffect(() => {
    setDefaultView(!newAccountsStore?.accountsState[AccountPageTypes.DRAFTS].userModified);
  }, [newAccountsStore?.accountsState, userModified]);

  const toggleFilterVisibility = () => {
    setFilterVisible(!filtersVisible);
  };

  const closeDeleteDialog = () => setAccountToDelete(null);

  const deleteAccount = (accountToDelete: DraftItem | null) => {
    if (!accountToDelete) {
      return;
    }
    cancelAccount(accountToDelete.accountId);
    closeDeleteDialog();
  };

  const redirectToDetailOrEdit = (
    accountId: string,
    status: AccountStatus,
    processing: boolean,
    isProtected?: boolean,
  ) => {
    if (isProtected) return;
    if (status === AccountStatus.AWAITING_APPROVAL) {
      history.push(routes.ACCOUNTS_DETAIL(accountId));
    } else if (status === AccountStatus.DRAFT && processing === true) {
      history.push(routes.ACCOUNTS_COMPLETE(accountId));
    } else if (status === AccountStatus.DRAFT || status === AccountStatus.MOCK) {
      history.push(routes.ACCOUNTS_EDIT(accountId));
    } else if (status === AccountStatus.TO_BE_SIGNED) {
      history.push(routes.ACCOUNTS_DOCUMENTS(accountId));
    }
  };

  const tablet = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'), { noSsr: true });
  const mid = useMediaQuery('(min-width:685px)', { noSsr: true });
  const laptop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'), { noSsr: true });
  const desktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'), { noSsr: true });
  const width = desktop
    ? Widths.DESKTOP
    : laptop
      ? Widths.LAPTOP
      : mid
        ? Widths.MID
        : tablet
          ? Widths.TABLET
          : Widths.MOBILE;

  if (!drafts) {
    return (
      <div className={classes.loadingContainer}>
        <LoadingIndicator size={56} />
      </div>
    );
  }

  return (
    <>
      <MaterialTable
        enhanced
        data={drafts}
        components={{
          Toolbar: (props) => (
            <Grid container>
              <MTableToolbar {...props} />
              <div>
                <Tooltip title={t('general_resetTableCols') || ''} placement="top">
                  <div>
                    <IconButton
                      size="small"
                      onClick={() => {
                        newAccountsStore && newAccountsStore.resetColumns(AccountPageTypes.DRAFTS);
                        setDefaultView(true);
                      }}
                    >
                      <RotateLeftIcon fontSize="large" />
                    </IconButton>
                  </div>
                </Tooltip>
                <Tooltip title={t('general_show_filters') || ''} placement="top">
                  <div>
                    <IconButton size="small" onClick={toggleFilterVisibility}>
                      <FilterIcon transparentBgr defaultFill fontSize="large" strokeWidth={0.1} />
                    </IconButton>
                  </div>
                </Tooltip>
                <Button variant="outlined" onClick={downloadAccounts} startIcon={<GetAppIcon />}>
                  {t('accounts_download_csv_short')}
                </Button>
              </div>
            </Grid>
          ),
        }}
        columns={sortColumns(
          defaultView,
          AccountPageTypes.DRAFTS,
          [
            {
              title: t('general_account'),
              field: 'clientName',
              hiddenByColumnsButton: true,
              cellStyle: { width: '25%' },
              render: ({ clientName, accountName }) => (
                <>
                  <span className={classes.name}>{clientName}</span>
                  <span className={classes.secondLine}>({accountName || t('accounts_withoutName')})</span>
                </>
              ),
              customFilterAndSearch: (filter, rowData, def) =>
                searchInStrings(filter, [rowData.clientName, rowData.accountName]),
              customSort: (a, b) => {
                return `${a.clientName} ${a.accountName}`.localeCompare(`${b.clientName} ${b.accountName}`);
              },
            },
            {
              hidden: defaultView && width < Widths.TABLET,
              title: t('accounts_singleInvestment'),
              field: 'singleInvestment',
              cellStyle: { ...rightCellStyles },
              headerStyle: { textAlign: 'right' },
              render: ({ singleInvestment, currency }) => formatAmountValue(singleInvestment, currency),
              customSort: (a, b) =>
                compareAmountValues(
                  { value: a.singleInvestment, currency: a.currency },
                  { value: b.singleInvestment, currency: b.currency },
                  newAccountsStore?.summary?.conversionRate,
                ),
            },
            {
              hidden: defaultView && width < Widths.TABLET,
              title: t('accounts_monthInvestment'),
              field: 'regularInvestment',
              cellStyle: { ...rightCellStyles },
              headerStyle: { textAlign: 'right' },
              render: ({ regularInvestment, currency }) => formatAmountValue(regularInvestment, currency),
              customSort: (a, b) =>
                compareAmountValues(
                  { value: a.regularInvestment, currency: a.currency },
                  { value: b.regularInvestment, currency: b.currency },
                  newAccountsStore?.summary?.conversionRate,
                ),
            },
            {
              hiddenByColumnsButton: true,
              title: t('accounts_status'),
              field: 'status',
              cellStyle: { ...centerCellStyles },
              headerStyle: { textAlign: 'center' },
              customFilterAndSearch: (search, data) => {
                return (
                  t(
                    data.isProtected
                      ? 'options_accountStatus_PROTECTED'
                      : `options_accountStatus_${data.status}${data.processing ? '_processing' : ''}`,
                  )
                    .toLowerCase()
                    .indexOf(search.trim().toLowerCase()) > -1
                );
              },
              render: ({ status, processing, isProtected }) => (
                <Tooltip
                  // title={t(`accounts_tooltip_${isProtected ? 'PROTECTED' : status || ''}`) || ''}
                  title={t(getStatusTooltipKey(status ?? '', isProtected, rootStore!.stores.userStore.loggedUser!.realm)) || ''}
                  placement="top"
                >
                  <span className={`${classes.status} status_${isProtected ? 'PROTECTED' : status}`}>
                    {t(`options_accountStatus_${isProtected ? 'PROTECTED' : status}${processing ? '_processing' : ''}`)}
                  </span>
                </Tooltip>
              ),

              customSort: (a, b) => {
                const statusSort = t(
                  a.isProtected ? '00' : `options_accountStatus_${a.status}${a.processing ? '_processing' : ''}`,
                ).localeCompare(
                  t(b.isProtected ? '00' : `options_accountStatus_${b.status}${b.processing ? '_processing' : ''}`),
                );
                return statusSort === 0 ? a.aum - b.aum : statusSort;
              },
            },
            {
              title: t('accounts_lastUpdate'),
              field: 'lastUpdate',
              cellStyle: { ...centerCellStyles },
              headerStyle: { textAlign: 'center' },

              customSort: ({ lastUpdate: firstDate }, { lastUpdate: secondDate }) =>
                compareCzechDates(firstDate, secondDate),
            },
            {
              hidden: defaultView && width < Widths.TABLET,
              title: t('accounts_clientView'),
              field: 'clientView',
              cellStyle: { ...centerCellStyles },
              headerStyle: { textAlign: 'center' },
              lookup: {
                [ClientView.NONE]: t('client_access_NONE'),
                [ClientView.BASIC]: t('client_access_BASIC'),
                [ClientView.FULL]: t('client_access_FULL'),
              },
              render: ({ clientView, twoFactor, status }) => {
                if (status !== AccountStatus.AWAITING_APPROVAL) return '';
                if (twoFactor === TwoFactorStatus.INCOMPLETE)
                  return (
                    <Tooltip title={t('accounts_access_SECOND_FACTOR_MISSING') || ''} placement="top">
                      <span>
                        <ErrorIcon htmlColor={EdwardColors.DARK} />
                      </span>
                    </Tooltip>
                  );
                if (twoFactor === TwoFactorStatus.NONE || clientView === ClientView.NONE || !clientView)
                  return (
                    <Tooltip title={t('accounts_access_ACCESS_NOT_ACTIVE') || ''} placement="top">
                      <span>
                        <AccessNone htmlColor="#999" defaultFill transparentBgr />
                      </span>
                    </Tooltip>
                  );
                return clientView === ClientView.FULL ? (
                  <Tooltip title={t('accounts_access_FULL_ACCESS') || ''} placement="top">
                    <span>
                      <AccessFull htmlColor="#000" defaultFill transparentBgr strokeWidth={0} />
                    </span>
                  </Tooltip>
                ) : (
                  <Tooltip
                    title={
                      t(
                        twoFactor === TwoFactorStatus.IRRELEVANT
                          ? 'accounts_access_SINGLE_FACTOR_ONLY'
                          : 'accounts_access_BASIC_ACCESS',
                      ) || ''
                    }
                    placement="top"
                  >
                    <CheckCircleIcon htmlColor={twoFactor === TwoFactorStatus.IRRELEVANT ? '#999' : undefined} />
                  </Tooltip>
                );
              },
            },
            {
              hiddenByColumnsButton: true,
              hidden: !newAccountsStore!.draftCommissions,
              field: 'commission',
              title: t('accounts_commissions'),
              lookup: {
                true: t('general_yes'),
                false: t('general_no'),
              },
              cellStyle: { ...centerCellStyles },
              headerStyle: { textAlign: 'center' },
              render: (account) =>
                account.hasCommission ? (
                  <Tooltip title={t('accounts_commissions') || ''} placement="top">
                    <IconButton
                      size="medium"
                      onClick={async (event) => {
                        event.stopPropagation();
                        rootStore!.stores.uiStore.setLoading(true);
                        const commissionData = await rootStore!.stores.apiStore.get<CommissionData>(
                          API_ACCOUNT_COMMISSIONS(account.accountId),
                        );
                        rootStore!.stores.uiStore.setLoading(false);
                        if (commissionData?.data) {
                          setCommission(commissionData.data);
                          setCommissionsOpen(true);
                        }
                      }}
                    >
                      <ExposureIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                ) : null,
            },
            {
              hiddenByColumnsButton: true,
              field: 'actions',
              render: (account) => {
                const isDraftOrMock = isDraftOrMockCheck(account.status);
                return (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                    }}
                  >
                    {width > Widths.TABLET && (
                      <Tooltip
                        title={
                          t(account.hasNotes ? 'accountNotes_title' : !account.isProtected ? '' : 'accountNotes_add') ||
                          ''
                        }
                        placement="top"
                      >
                        <IconButton
                          size="medium"
                          onClick={(event) => {
                            event.stopPropagation();
                            rootStore!.stores.uiStore.openNotes(account.accountId);
                          }}
                          disabled={account.isProtected && !account.hasNotes}
                        >
                          {account.hasNotes ? (
                            <NoteIcon
                              fontSize="small"
                              htmlColor={EdwardColors.SECONDARY}
                              defaultFill
                              transparentBgr
                              strokeWidth={0}
                            />
                          ) : (
                            <NoteIcon
                              fontSize="small"
                              htmlColor={account.isProtected ? EdwardColors.GRAY_LIGHTER : EdwardColors.GRAY_DARKER}
                              defaultFill
                              transparentBgr
                              strokeWidth={0}
                            />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                    {rootStore!.stores.userStore.loggedUser!.realm !== 'bt' && (
                      <Tooltip title={t('accounts_copy') || ''} placement="top">
                        <span>
                          <IconButton
                            size="medium"
                            className="accountDetail-button"
                            disabled={
                              account.status !== AccountStatus.DRAFT && account.status !== AccountStatus.TO_BE_SIGNED
                            }
                            onClick={async (event) => {
                              event.stopPropagation();
                              await copyAccount(account.accountId);
                            }}
                          >
                            <FileCopyOutlinedIcon fontSize="inherit" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}

                    {(account.status === AccountStatus.ACTIVE || account.status === AccountStatus.AWAITING_APPROVAL) &&
                      width > Widths.MOBILE && (
                        <Tooltip title={t('accounts_detail') || ''} placement="top">
                          <IconButton
                            size="medium"
                            component={Link}
                            className="accountDetail-button"
                            to={routes.ACCOUNTS_DETAIL(account.accountId)}
                          >
                            <DetailIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      )}

                    {account.status === AccountStatus.TO_BE_SIGNED && width > Widths.MOBILE && (
                      <Tooltip title={t('accounts_to_be_signed') || ''} placement="top">
                        <span>
                          {/* Span tady musi byt kvuli moznemu disabled na tlacitku niz (MUI pak nedostane hover eventy) */}
                          <IconButton
                            size="medium"
                            component={Link}
                            className="accountDetail-button"
                            to={routes.ACCOUNTS_DOCUMENTS(account.accountId)}
                          >
                            <DetailIcon fontSize="inherit" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}

                    {isDraftOrMock && width > Widths.MOBILE && (
                      <Tooltip
                        title={t(account.processing ? 'accounts_processing' : 'accounts_edit') || ''}
                        placement="top"
                      >
                        <IconButton
                          disabled={account.isProtected}
                          size="medium"
                          component={Link}
                          className="accountEdit-button"
                          to={
                            account.processing
                              ? routes.ACCOUNTS_COMPLETE(account.accountId)
                              : routes.ACCOUNTS_EDIT(account.accountId)
                          }
                        >
                          {account.processing ? <RefreshIcon fontSize="inherit" /> : <EditIcon fontSize="inherit" />}
                        </IconButton>
                      </Tooltip>
                    )}
                    {rootStore && rootStore.stores.userStore.checkPermissions(Permission.ACCOUNT_DRAFT_REMOVE) && (
                      <Tooltip
                        title={isDraftOrMock && !account.isProtected ? t('accounts_delete') : ''}
                        placement="top"
                      >
                        <span>
                          {/* Span tady musi byt kvuli moznemu disabled na tlacitku niz (MUI pak nedostane hover eventy) */}
                          <IconButton
                            size="medium"
                            className="account-delete-button"
                            disabled={!isDraftOrMock || account.processing || account.isProtected}
                            onClick={(event) => {
                              setAccountToDelete(account);
                              event.stopPropagation();
                            }}
                          >
                            <DeleteIcon fontSize="inherit" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </div>
                );
              },
              cellStyle: {
                width: width > Widths.TABLET ? '10%' : '5%',
              },
            },
          ],
          Order.DESC,
          4,
        )}
        options={{
          searchFieldAlignment: 'left',
          headerStyle: {
            borderBottom: '2px solid rgb(224, 224, 224)',
          },
          pageSize: Math.min(pageSize, 50), //EDS-3894
          filtering: filtersVisible,
          sorting: true,
          columnsButton: true,
          rowStyle: (rowData) => {
            const isMock = rowData.status === AccountStatus.MOCK;
            return {
              backgroundColor: rowData.isProtected
                ? palette.grey['50']
                : isMock
                  ? palette.grey['100']
                  : palette.common.white,
              color: isMock ? palette.grey.A400 : palette.text.primary,
            };
          },
          searchText: state.search,
        }}
        onRowClick={(_event, { accountId, status, processing, isProtected }) => {
          redirectToDetailOrEdit(accountId, status, processing, isProtected);
        }}
        onSearchChange={(search) => {
          onTableSearchChange(AccountPageTypes.DRAFTS, search);
        }}
        onOrderChange={(orderedColumnId, orderDirection) =>
          onTableOrderChange(AccountPageTypes.DRAFTS, orderedColumnId, orderDirection as Order)
        }
        onRowsPerPageChange={onChangeRowsPerPage}
      />
      <ConfirmDialog
        title="accounts_delete"
        btnConfirmText="general_delete"
        isOpen={accountToDelete !== null}
        close={closeDeleteDialog}
        confirm={() => deleteAccount(accountToDelete)}
      >
        {accountToDelete && (
          <Typography>
            {t('accounts_delete_confirm_body')}{' '}
            <strong>
              {accountToDelete.accountName} ({accountToDelete.clientName})
            </strong>
            ?
          </Typography>
        )}
      </ConfirmDialog>

      <GenerationDialog store={newAccountsStore!} />

      {commissionsOpen && commission && (
        <Commissions
          handleClose={setCommissionsOpen}
          commissionsToken={commission.token}
          clientName={commission.client}
          commissionId={commission.commissionId}
          contractId={commission.contractNumber}
        />
      )}
    </>
  );
};

export default inject('newAccountsStore', 'rootStore')(observer(Drafts));
